import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  HStack,
  VStack,
  Stack,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  IconButton,
  Select,
  Checkbox,
  Badge,
  Link,
  Spinner,
  Divider,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  Collapse,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { SearchIcon, ExternalLinkIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { FiFilter } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useQuery } from 'react-query';
import {
  regulatoryApi,
  RegulatorySource,
  RegulatorySearchResponse,
} from '../services/api';

// Mapeia o status de vigência para cor — a sinalização visual que diferencia
// um documento em vigor de um revogado/indeterminado é central no produto.
const statusColorScheme = (status?: string): string => {
  const s = (status || '').toLowerCase();
  if (s.includes('vigente') || s.includes('vigor')) return 'green';
  if (s.includes('revogad')) return 'red';
  if (s.includes('suspens')) return 'orange';
  return 'gray'; // indeterminado / desconhecido
};

const statusLabel = (status?: string): string => {
  if (!status) return 'indeterminado';
  return status;
};

// Formata a vigência (data de emissão) de forma curta e tolerante a nulos.
const formatDate = (iso?: string): string | null => {
  if (!iso) return null;
  // Aceita 'yyyy-mm-dd' ou ISO completo; pega só a parte da data.
  const datePart = iso.split('T')[0];
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

// Cartão de uma fonte recuperada — exibe a citação granular (cláusula/página),
// a vigência, o link original e o trecho. É a trilha de auditoria de F1.
const SourceCard = ({ source, index }: { source: RegulatorySource; index: number }) => {
  const date = formatDate(source.date_issued);
  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={4}
      bg="white"
      shadow="sm"
      _hover={{ shadow: 'md', borderColor: 'brand.200' }}
      transition="all 0.15s"
    >
      <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
        <HStack spacing={2} align="center">
          <Badge colorScheme="brand" borderRadius="full" px={2}>
            {index + 1}
          </Badge>
          <Text fontWeight="semibold" noOfLines={2}>
            {source.title || 'Documento sem título'}
          </Text>
        </HStack>
        <Badge colorScheme={statusColorScheme(source.status)} variant="subtle">
          {statusLabel(source.status)}
        </Badge>
      </Flex>

      {/* Metadados / citação granular */}
      <Wrap spacing={2} mt={2} fontSize="sm" color="gray.600">
        {source.agency && (
          <WrapItem>
            <Tag size="sm" colorScheme="blue" variant="subtle">
              <TagLabel>{source.agency}</TagLabel>
            </Tag>
          </WrapItem>
        )}
        {source.doc_type && (
          <WrapItem>
            <Tag size="sm" variant="subtle">
              <TagLabel>{source.doc_type}</TagLabel>
            </Tag>
          </WrapItem>
        )}
        {source.section && (
          <WrapItem>
            <Tag size="sm" colorScheme="purple" variant="subtle">
              <TagLabel>{source.section}</TagLabel>
            </Tag>
          </WrapItem>
        )}
        {source.page != null && (
          <WrapItem>
            <Tag size="sm" variant="outline">
              <TagLabel>p. {source.page}</TagLabel>
            </Tag>
          </WrapItem>
        )}
        {date && (
          <WrapItem>
            <Text>{date}</Text>
          </WrapItem>
        )}
        {source.relevance_score != null && (
          <WrapItem>
            <Text color="gray.400">
              relevância {source.relevance_score.toFixed(2)}
            </Text>
          </WrapItem>
        )}
      </Wrap>

      {source.snippet && (
        <Text mt={3} fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
          {source.snippet}
        </Text>
      )}

      {source.url_original && (
        <Link
          href={source.url_original}
          isExternal
          color="brand.600"
          fontSize="sm"
          mt={2}
          display="inline-flex"
          alignItems="center"
          gap={1}
        >
          Documento original <ExternalLinkIcon />
        </Link>
      )}
    </Box>
  );
};

const RegulatorySearch = () => {
  const toast = useToast();
  const { isOpen: filtersOpen, onToggle: toggleFilters } = useDisclosure();

  // Estado da busca
  const [query, setQuery] = useState('');
  const [agency, setAgency] = useState('');
  const [docType, setDocType] = useState('');
  const [status, setStatus] = useState('');
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [maxResults, setMaxResults] = useState(10);

  // Resultado
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RegulatorySearchResponse | null>(null);
  const [searched, setSearched] = useState(false);

  // Opções de filtro vindas do backend (ontologia). Falha silenciosa: se a
  // chamada não responder, os selects ficam vazios e a busca segue sem filtro.
  const { data: agencies = [] } = useQuery('reg-agencies', regulatoryApi.getAgencies, {
    staleTime: Infinity,
  });
  const { data: docTypes = [] } = useQuery('reg-doctypes', regulatoryApi.getDocTypes, {
    staleTime: Infinity,
  });

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < 2) {
      toast({
        title: 'Digite ao menos 2 caracteres',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await regulatoryApi.search({
        query: q,
        max_results: maxResults,
        include_revoked: includeRevoked,
        filters: {
          // Só envia o que foi selecionado — listas vazias = sem filtro.
          agency: agency ? [agency] : [],
          doc_type: docType ? [docType] : [],
          status: status || null,
        },
      });
      setResult(response);
    } catch (error: any) {
      toast({
        title: 'Erro na busca regulatória',
        description:
          error?.response?.data?.detail || 'Não foi possível concluir a busca.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      runSearch();
    }
  };

  const hasResults = result && result.sources.length > 0;

  return (
    <Box>
      <VStack align="stretch" spacing={1} mb={4}>
        <Heading size="lg" color="brand.700">
          Busca Regulatória
        </Heading>
        <Text color="gray.600">
          Pesquisa no corpus regulatório com citação por cláusula e página. Cada
          fonte traz a vigência e o link para o documento original.
        </Text>
      </VStack>

      {/* Barra de busca */}
      <Box bg="white" borderWidth="1px" borderRadius="lg" p={4} shadow="sm">
        <InputGroup size="lg">
          <Input
            placeholder="Ex.: como funciona o reajuste tarifário anual?"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <InputRightElement width="3rem">
            <IconButton
              aria-label="Buscar"
              icon={<SearchIcon />}
              colorScheme="brand"
              size="sm"
              onClick={runSearch}
              isLoading={loading}
            />
          </InputRightElement>
        </InputGroup>

        <Flex justify="space-between" align="center" mt={3}>
          <Button
            leftIcon={<FiFilter />}
            rightIcon={filtersOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            variant="ghost"
            size="sm"
            onClick={toggleFilters}
          >
            Filtros
          </Button>
          <Button
            colorScheme="brand"
            size="sm"
            onClick={runSearch}
            isLoading={loading}
            loadingText="Buscando"
          >
            Buscar
          </Button>
        </Flex>

        <Collapse in={filtersOpen} animateOpacity>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            spacing={4}
            mt={4}
            pt={4}
            borderTopWidth="1px"
          >
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Agência
              </Text>
              <Select
                placeholder="Todas"
                value={agency}
                onChange={(e) => setAgency(e.target.value)}
                size="sm"
              >
                {agencies.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </Select>
            </Box>

            <Box flex={1}>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Tipo de documento
              </Text>
              <Select
                placeholder="Todos"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                size="sm"
              >
                {docTypes.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </Box>

            <Box flex={1}>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Status
              </Text>
              <Select
                placeholder="Qualquer"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                size="sm"
              >
                <option value="vigente">Vigente</option>
                <option value="revogado">Revogado</option>
                <option value="indeterminado">Indeterminado</option>
              </Select>
            </Box>

            <Box flex={1}>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Máx. resultados
              </Text>
              <Select
                value={maxResults}
                onChange={(e) => setMaxResults(Number(e.target.value))}
                size="sm"
              >
                {[5, 10, 20, 30, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
            </Box>
          </Stack>

          <Checkbox
            mt={3}
            isChecked={includeRevoked}
            onChange={(e) => setIncludeRevoked(e.target.checked)}
            colorScheme="brand"
          >
            <Text fontSize="sm">Incluir documentos revogados</Text>
          </Checkbox>
        </Collapse>
      </Box>

      {/* Resultado */}
      {loading && (
        <Flex justify="center" align="center" py={16} direction="column" gap={3}>
          <Spinner size="lg" color="brand.500" thickness="3px" />
          <Text color="gray.500">Buscando no corpus regulatório…</Text>
        </Flex>
      )}

      {!loading && searched && !hasResults && (
        <Box textAlign="center" py={16} color="gray.500">
          <Text fontSize="lg">Nenhum resultado encontrado.</Text>
          <Text fontSize="sm" mt={1}>
            Tente reformular a pergunta ou afrouxar os filtros.
          </Text>
        </Box>
      )}

      {!loading && result && (
        <VStack align="stretch" spacing={5} mt={5}>
          {/* Resposta sintetizada (quando disponível) */}
          {result.answer && (
            <Box
              borderWidth="1px"
              borderRadius="lg"
              borderColor="brand.200"
              bg="brand.50"
              p={5}
            >
              <Heading size="sm" color="brand.700" mb={2}>
                Resposta
              </Heading>
              <Box className="markdown-content" fontSize="sm" color="gray.800">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {result.answer}
                </ReactMarkdown>
              </Box>
            </Box>
          )}

          {hasResults && (
            <Box>
              <Flex align="center" gap={2} mb={3}>
                <Heading size="sm" color="gray.700">
                  Fontes
                </Heading>
                <Badge colorScheme="brand" borderRadius="full">
                  {result.sources.length}
                </Badge>
              </Flex>
              <VStack align="stretch" spacing={3}>
                {result.sources.map((s, i) => (
                  <SourceCard key={s.document_id ? `${s.document_id}-${i}` : i} source={s} index={i} />
                ))}
              </VStack>
            </Box>
          )}
        </VStack>
      )}

      {!searched && !loading && (
        <Box textAlign="center" py={16} color="gray.400">
          <Divider mb={8} />
          <Text>Faça uma pergunta para começar a busca regulatória.</Text>
        </Box>
      )}
    </Box>
  );
};

export default RegulatorySearch;
